import {Button, CircularProgress, IconButton, Typography} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import SettingsIcon from "@material-ui/icons/Settings";
import {ChangeEvent, DragEvent, MouseEvent, useEffect, useRef, useState} from "react";
import {useFormState} from "react-final-form";
import {BooleanInput, useNotify} from "react-admin";
import {FiUploadCloud} from "react-icons/fi";
import {clientNoJson, endpoint} from "../../dataProvider";
import {UTIF} from "../Note/UTIF_MOD";
import {initInput} from "../../shared/utils";

export type RoiItem = {
    name: string;
    bbox?: [number, number, number, number];
    size?: { width: number; height: number };
};

type Props = {
    onChange: (items: RoiItem[]) => void;
    onOpenSettings: () => void;
    fieldOptions: any;
    canEdit?: boolean;
};

export const NoteConfigurationUploader = ({onChange, onOpenSettings, fieldOptions, canEdit}: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imageRef = useRef<ImageData | null>(null);
    const [items, setItems] = useState<RoiItem[]>([]);
    const [index, setIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState("");
    const [drawingStart, setDrawingStart] = useState<{ x: number; y: number } | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const notify = useNotify();
    const {values} = useFormState({subscription: {values: true}});

    const updateItems = (next: RoiItem[]) => {
        setItems(next);
        onChange(next);
    };

    const draw = (item?: RoiItem, preview?: [number, number, number, number]) => {
        const canvas = canvasRef.current;
        const image = imageRef.current;
        if (!canvas || !image) return;
        canvas.width = image.width;
        canvas.height = image.height;
        const context = canvas.getContext("2d");
        if (!context) return;
        context.putImageData(image, 0, 0);
        const bbox = preview || item?.bbox;
        if (bbox) {
            const [left, top, right, bottom] = bbox;
            context.fillStyle = "rgba(25, 118, 210, .16)";
            context.fillRect(left, top, right - left, bottom - top);
            context.strokeStyle = "#1976d2";
            context.lineWidth = Math.max(2, Math.round(canvas.width / 1000));
            context.setLineDash([10, 6]);
            context.strokeRect(left, top, right - left, bottom - top);
            context.setLineDash([]);
        }
    };

    useEffect(() => {
        const item = items[index];
        if (!item) {
            imageRef.current = null;
            return;
        }
        let active = true;
        clientNoJson(`${endpoint}/note-configurations/temporary-files/${encodeURIComponent(item.name)}/`, {
            method: "GET",
            responseType: "arraybuffer",
        }).then(({data}) => {
            if (!active) return;
            const ifds = UTIF.decode(data);
            UTIF.decodeImage(data, ifds[0], ifds);
            const rgba = UTIF.toRGBA8(ifds[0]);
            imageRef.current = new ImageData(new Uint8ClampedArray(rgba.buffer), ifds[0].width, ifds[0].height);
            const previous = items[index - 1];
            if (!item.bbox && previous?.bbox && previous.size) {
                const [left, top, right, bottom] = previous.bbox;
                const inherited: RoiItem = {
                    ...item,
                    bbox: [
                        Math.round(left / previous.size.width * ifds[0].width),
                        Math.round(top / previous.size.height * ifds[0].height),
                        Math.round(right / previous.size.width * ifds[0].width),
                        Math.round(bottom / previous.size.height * ifds[0].height),
                    ],
                    size: {width: ifds[0].width, height: ifds[0].height},
                };
                const next = items.map((value, itemIndex) => itemIndex === index ? inherited : value);
                updateItems(next);
                draw(inherited);
            } else {
                draw(item);
            }
        }).catch(() => notify("Impossibile mostrare il file caricato", "warning"));
        return () => {
            active = false;
        };
    }, [index, items.length]); // the ROI is redrawn explicitly after each selection

    const uploadFiles = async (files: File[]) => {
        if (!files.length) return;
        setLoading(true);
        try {
            const uploaded: RoiItem[] = [];
            for (const file of files) {
                const data = new FormData();
                data.append("file", file);
                const response = await clientNoJson(`${endpoint}/note-configurations/upload/`, {
                    method: "POST",
                    data,
                    headers: {"Content-Type": "multipart/form-data"},
                });
                (response.data.files || []).forEach((uploadedFile: {
                    name: string
                }) => uploaded.push({name: uploadedFile.name}));
            }
            const next = [...items, ...uploaded];
            updateItems(next);
            setIndex(items.length);
        } catch (error) {
            notify("Errore durante il caricamento del file", "warning");
        } finally {
            setLoading(false);
        }
    };

    const upload = async (event: ChangeEvent<HTMLInputElement>) => {
        await uploadFiles(Array.from(event.target.files || []));
        event.target.value = "";
    };

    const dropFiles = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragActive(false);
        uploadFiles(Array.from(event.dataTransfer.files || []));
    };

    const pointFor = (event: MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current!;
        const rect = canvas.getBoundingClientRect();
        return {
            x: Math.max(0, Math.min(canvas.width, Math.round((event.clientX - rect.left) * canvas.width / rect.width))),
            y: Math.max(0, Math.min(canvas.height, Math.round((event.clientY - rect.top) * canvas.height / rect.height))),
        };
    };

    const completeSelection = (event: MouseEvent<HTMLCanvasElement>) => {
        if (!drawingStart || !items[index] || !canvasRef.current) return;
        const end = pointFor(event);
        const bbox: [number, number, number, number] = [
            Math.min(drawingStart.x, end.x), Math.min(drawingStart.y, end.y),
            Math.max(drawingStart.x, end.x), Math.max(drawingStart.y, end.y),
        ];
        if (bbox[2] - bbox[0] < 2 || bbox[3] - bbox[1] < 2) {
            setDrawingStart(null);
            return;
        }
        const next = items.map((item, itemIndex) => itemIndex === index
            ? {...item, bbox, size: {width: canvasRef.current!.width, height: canvasRef.current!.height}}
            : item);
        updateItems(next);
        setDrawingStart(null);
        draw(next[index]);
    };

    const previewSelection = (event: MouseEvent<HTMLCanvasElement>) => {
        if (!drawingStart || !items[index]) return;
        const end = pointFor(event);
        draw(items[index], [
            Math.min(drawingStart.x, end.x), Math.min(drawingStart.y, end.y),
            Math.max(drawingStart.x, end.x), Math.max(drawingStart.y, end.y),
        ]);
    };

    const removeCurrent = async () => {
        const item = items[index];
        if (!item) return;
        try {
            await clientNoJson(`${endpoint}/note-configurations/temporary-files/${encodeURIComponent(item.name)}/`, {method: "DELETE"});
        } finally {
            const next = items.filter((_, itemIndex) => itemIndex !== index);
            updateItems(next);
            setIndex(Math.max(0, Math.min(index, next.length - 1)));
        }
    };

    const testCurrent = async () => {
        if (!current?.bbox || !current.size) {
            notify("Seleziona prima la ROI da testare", "warning");
            return;
        }
        setTesting(true);
        try {
            const response = await clientNoJson(`${endpoint}/note-configurations/test-ocr/`, {
                method: "POST",
                data: {
                    filename: current.name,
                    metadata: {
                        roi: {bbox: current.bbox},
                        rotate: values.rotate_image || 0,
                        replace: values.replace,
                        replaceTo: values.replace_to,
                        removeSpaces: values.remove_space,
                        qrMode: values.qr_mode,
                        charWhitelist: values.char_whitelist,
                        preprocessing: {
                            rescale: values.preprocess_rescale,
                            binarize: values.preprocess_binarize,
                            denoise: values.preprocess_denoise,
                            morphology: values.preprocess_morphology,
                            deskew: values.preprocess_deskew,
                        },
                    },
                },
            });
            setTestResult(response.data.text || "Nessun testo rilevato");
        } catch (error) {
            notify("Errore durante il test OCR", "warning");
        } finally {
            setTesting(false);
        }
    };

    const current = items[index];
    return <div style={{width: "100%", marginTop: 24}}>
        <input ref={inputRef} type="file" accept=".tif,.tiff,application/pdf" multiple hidden onChange={upload}/>
        <div role="button" tabIndex={0} onClick={() => !loading && inputRef.current?.click()}
             onKeyDown={(event) => event.key === "Enter" && !loading && inputRef.current?.click()}
             onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
             onDragOver={(event) => event.preventDefault()}
             onDragLeave={() => setDragActive(false)} onDrop={dropFiles}
             style={{padding: "32px 16px", border: `2px dashed ${dragActive ? "#1976d2" : "#9e9e9e"}`, borderRadius: 8, textAlign: "center", cursor: loading ? "wait" : "pointer", backgroundColor: dragActive ? "#e3f2fd" : "#fafafa", opacity: loading ? .7 : 1}}>
            {loading ? <CircularProgress size={28}/> : <FiUploadCloud size={30} color="#1976d2"/>}
            <Typography variant="subtitle1">Trascina qui TIFF o PDF, oppure clicca per selezionarli</Typography>
            <Typography variant="body2" color="textSecondary">Puoi caricare più pagine insieme</Typography>
        </div>

        {current && <>
            <div style={{display: "flex", alignItems: "center", gap: 8, marginTop: 16}}>
                <Button onClick={() => setIndex(index - 1)} disabled={index === 0}><NavigateBeforeIcon/></Button>
                <Typography>{index + 1} / {items.length} — {current.name}{current.bbox ? " (ROI selezionata)" : ""}</Typography>
                <Button onClick={() => setIndex(index + 1)}
                        disabled={index >= items.length - 1}><NavigateNextIcon/></Button>
                <Button variant="contained" color="primary" onClick={testCurrent} disabled={testing}>
                    {testing ? "Test in corso..." : "Test"}
                </Button>
                {testResult && <Typography variant="body1" style={{fontStyle: "bold", whiteSpace: "pre-wrap", paddingLeft:12, paddingRight:12, border: "1px solid #ddd", borderRadius: 20, paddingTop: 8, paddingBottom: 8, backgroundColor: "#f5f5f5"}}>
                    {testResult}
                </Typography>}
                <div style={{marginLeft: "auto", display: "flex", alignItems: "center", gap: 8}}>
                    <BooleanInput {...initInput("qr_mode", fieldOptions, canEdit)} label="QR mode"/>
                    <IconButton aria-label="Impostazioni configurazione" title="Impostazioni configurazione" onClick={onOpenSettings} disabled={canEdit === false}><SettingsIcon/></IconButton>
                    <IconButton aria-label="Elimina file" title="Elimina file" color="secondary" onClick={removeCurrent}><DeleteIcon/></IconButton>
                </div>
            </div>
            <div style={{maxHeight: "60vh", overflow: "auto", marginTop: 8, border: "1px solid #ddd"}}>
                <canvas
                    ref={canvasRef}
                    onMouseDown={(event) => {
                        const point = pointFor(event);
                        setDrawingStart(point);
                    }}
                    onMouseMove={previewSelection}
                    onMouseUp={completeSelection}
                    onMouseLeave={(event) => drawingStart && completeSelection(event)}
                    style={{display: "block", width: "100%", cursor: "crosshair"}}
                />
            </div>
        </>}
    </div>;
};
